import {
  // Input,
  Form,
  Button,
  ConfigProvider,
  theme,
  Select,
  Checkbox,
  // Radio,
  // Space,
} from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Selection from './Selection';
import PrintSettingsExtendForm from './PrintSettingsExtendForm';
import {
  FILE_TYPE_OPTIONS,
  FILM_TYPE,
  ORIENTATION,
  PRINT_TYPE,
  PrintExtraSettings,
  PrintSettings,
  TEXT_SIZE,
} from '@vindr/services/constants/print.constant';

interface PrintSettingsFormProps {
  onRatioChange: (size: number) => unknown;
  onTextSizeChange: (size: string) => unknown;
  onChangeTextOverLay: (isEnabled: boolean) => unknown;
  onOrientationChange: (orientation: string) => unknown;
  onPrint: (settings: PrintSettings) => unknown;
  onError: (message: string) => unknown;
  onSuccess: (message: string) => unknown;
}

const PrintSettingsForm = ({
  onRatioChange,
  onTextSizeChange,
  onChangeTextOverLay,
  onOrientationChange,
  onPrint,
}: PrintSettingsFormProps) => {
  const { t } = useTranslation('ViewerTool');
  const [form] = Form.useForm<PrintSettings>();

  const [downloading, setDownloading] = useState(false);
  const [textOverlay, setTextOverlay] = useState(false);
  const [extraSettings, setExtraSettings] = useState({});
  const [openExtraSettings, setOpenExtraSettings] = useState(false);

  const handleUpdateSettings = (values: PrintExtraSettings) => {
    setOpenExtraSettings(false);
    if (values.orientation !== form.getFieldValue('orientation')) {
      onOrientationChange(values.orientation);
    }
    if (values.film_size !== form.getFieldValue('film_size')) {
      onSizeChange(values.film_size);
    }
    setExtraSettings(values);
    form.setFieldsValue(values);
  };

  const handlePrint = async () => {
    setDownloading(true);
    const values = await form.validateFields();

    await onPrint({ ...extraSettings, ...values });
    setDownloading(false);
  };

  const onSizeChange = (value: string) => {
    const sizeType = FILM_TYPE.find((it) => it?.key === value);

    if (!sizeType) return;
    onRatioChange(sizeType.ratio);
  };

  return (
    <ConfigProvider
      theme={{
        algorithm: theme.darkAlgorithm,
        token: {
          fontSize: 12,
          borderRadiusSM: 2,
          borderRadiusLG: 2,
          borderRadius: 2,
          colorPrimary: '#39C2D7',
          colorPrimaryHover: '#2799b0',
          colorText: '#fff',
          colorBgBase: '#172126',
          colorBgContainer: 'transparent',
          colorBorder: '#647a87',
          controlItemBgActiveDisabled: '#2C4654',
          controlItemBgActive: '#2C4654',
          controlOutlineWidth: 0,
          colorBgElevated: '#1F313B',
        },
        components: {
          Menu: {
            colorItemBg: 'transparent',
            colorItemBgSelected: '#2c5166',
          },
        },
      }}
    >
      <div className="flex w-full justify-between">
        <div className="mx-2 flex w-full flex-col">
          <Form
            form={form}
            size="small"
            layout="vertical"
            initialValues={{
              type: PRINT_TYPE.DICOM,
              number_of_copies: 1,
              text_size: 'md',
              orientation: 'PORTRAIT',
              film_size: FILM_TYPE[0].key,
              medium_type: 'CLEAR FILM',
              magnification: 'BILINEAR',
              trim: 'NO',
              // destination: 'PROCESSOR',
              // priority: 'MED',
              density_max: '65535',
              density_min: '0',
              density_border: 'BLACK',
              density_empty: 'BLACK',
            }}
          >
            <div className="w-full">
              <div className="mb-2 border-b border-secondary-main text-lg">
                {t('Paper')}
              </div>
              <div className="grid">
                {/* <Form.Item className="mb-2">
                  <Checkbox
                    onChange={(e) => {
                      console.log(e);
                    }}
                  >
                    {t('Patient Info')}
                  </Checkbox>
                </Form.Item> */}
                <Form.Item className="mb-2">
                  <Checkbox
                    checked={textOverlay}
                    onChange={(e) => {
                      setTextOverlay(e.target.checked);
                      onChangeTextOverLay(e.target.checked);
                    }}
                  >
                    {t('Demographic Information')}
                  </Checkbox>
                </Form.Item>
                {/* <Form.Item className="mb-2">
                  <Checkbox
                    onChange={(e) => {
                      console.log(e);
                    }}
                  >
                    {t('Exam Info')}
                  </Checkbox>
                </Form.Item> */}
                {/* <Form.Item className="mb-2">
                  <Checkbox
                    onChange={(e) => {
                      console.log(e);
                    }}
                  >
                    {t('Page Number')}
                  </Checkbox>
                </Form.Item> */}
              </div>
            </div>
            <div className="w-full">
              <div className="mb-2 border-b border-secondary-main text-lg">
                {t('Print Type')}
              </div>
              <div className="flex w-full">
                <Form.Item
                  className="mb-2 mr-2 w-full"
                  name="type"
                  initialValue={PRINT_TYPE.DICOM}
                >
                  <Selection
                    className="w-full text-white"
                    isClearable={false}
                    // value={fileType}
                    data-cy="file-type"
                    onChange={(value) => {
                      // Change print type
                    }}
                    hideSelectedOptions={false}
                    placeholder={t('File Type')}
                  >
                    {FILE_TYPE_OPTIONS.map((it) => (
                      <Select.Option key={it.value} value={it.value}>
                        {t(it.label)}
                      </Select.Option>
                    ))}
                  </Selection>
                </Form.Item>
                <Button
                  className="mb-2"
                  onClick={() => setOpenExtraSettings((val) => !val)}
                >
                  {t('Settings')}
                </Button>
              </div>

              <div className="flex w-full">
                <Form.Item
                  className="mb-2 w-full"
                  label={t('Number Of Copies') + ': '}
                  name="number_of_copies"
                  initialValue={1}
                >
                  <Selection
                    className="w-full text-white"
                    isClearable={false}
                    data-cy="file-type"
                    onChange={(value) => {
                      // setFileType([value.value]);
                    }}
                    hideSelectedOptions={false}
                    placeholder={t('File Type')}
                  >
                    {[1, 2, 3, 4, 5, 10, 15, 20].map((it) => (
                      <Select.Option key={it} value={it}>
                        {it}
                      </Select.Option>
                    ))}
                  </Selection>
                </Form.Item>
              </div>
              <div className="flex w-full">
                <Form.Item
                  className="mb-2 w-full"
                  label={t('Text Size') + ': '}
                  name="text_size"
                  initialValue={'md'}
                >
                  <Selection
                    className="w-full text-white"
                    isClearable={false}
                    // value={textSize}
                    data-cy="text-size"
                    onChange={(value) => {
                      onTextSizeChange(value);
                    }}
                    hideSelectedOptions={false}
                    placeholder={t('File Type')}
                  >
                    {TEXT_SIZE.map((it) => (
                      <Select.Option key={it.value} value={it.value}>
                        {t(it.label)}
                      </Select.Option>
                    ))}
                  </Selection>
                </Form.Item>
              </div>
              <div className="flex w-full">
                <Form.Item
                  className="mb-2 w-full"
                  label={t('Orientation') + ': '}
                  name="orientation"
                  initialValue={'PORTRAIT'}
                >
                  <Selection
                    className="w-full text-white"
                    isClearable={false}
                    // value={orientation}
                    data-cy="file-type"
                    onChange={(value) => {
                      onOrientationChange(value);
                    }}
                    hideSelectedOptions={false}
                    placeholder={t('Orientation')}
                  >
                    {ORIENTATION.map((it) => (
                      <Select.Option key={it.key} value={it.key}>
                        {t(it.title)}
                      </Select.Option>
                    ))}
                  </Selection>
                </Form.Item>
              </div>
              <div className="flex w-full">
                <Form.Item
                  className="mb-2 w-full"
                  label={t('Film Size') + ': '}
                  name="film_size"
                  initialValue={FILM_TYPE[0].key}
                >
                  <Selection
                    className="w-full text-white"
                    isClearable={false}
                    // value={filmType}
                    data-cy="file-type"
                    onChange={(value) => {
                      onSizeChange(value);
                    }}
                    hideSelectedOptions={false}
                    placeholder={t('File Type')}
                  >
                    {FILM_TYPE.map((it) => (
                      <Select.Option key={it.key} value={it.key}>
                        {t(it.title)}
                      </Select.Option>
                    ))}
                  </Selection>
                </Form.Item>
              </div>
              {/* Feature not support yet */}
              {/* <div className="flex w-full">
                <Form.Item
                  className="mb-2 w-full"
                  label={t('Print Page Range') + ': '}
                >
                  <Radio.Group
                    className="w-full text-white"
                    data-cy="file-type"
                    onChange={(value) => {
                      // setFileType([value.value]);
                    }}
                  >
                    <Space direction="vertical">
                      <Radio value={'All'}>{t('All')}</Radio>
                      <Radio value={'Custom'}>
                        <div className="flex">
                          <span>{t('Select')}</span>
                          <Input
                            size="small"
                            type="number"
                            className="mx-2"
                            min={1}
                            max={100}
                            defaultValue={1}
                          />
                          <span>{t('to')}</span>
                          <Input
                            size="small"
                            type="number"
                            className="mx-2"
                            min={1}
                            max={100}
                            defaultValue={10}
                          />
                        </div>
                      </Radio>
                      <Radio value={'Current'}>{t('Current')}</Radio>
                    </Space>
                  </Radio.Group>
                </Form.Item>
              </div> */}
            </div>
          </Form>
          <div className="mt-4 flex">
            {/* <Button data-cy="cancel-btn" onClick={onClose} className="px-2">
              {t('Cancel')}
            </Button> */}
            <Button
              size="small"
              loading={downloading}
              className="mx-2 w-full"
              onClick={handlePrint}
              type="primary"
              data-cy="download-btn"
            >
              {downloading ? t('Saving') : t('Save')}
            </Button>
          </div>
          <PrintSettingsExtendForm
            open={openExtraSettings}
            onCancel={() => setOpenExtraSettings(false)}
            onOk={handleUpdateSettings}
            settings={form.getFieldsValue(true)}
          />
        </div>
      </div>
    </ConfigProvider>
  );
};

export default PrintSettingsForm;
